import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import CartButton from "./cart/CartButton";
import CartSidebar from "./cart/CartSidebar";
import OpeningHours from "./OpeningHours";
import { Menu, Mountain, Phone, Clock } from "lucide-react";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [tourBookingEnabled, setTourBookingEnabled] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // Fetch tour booking status
    const fetchTourBookingStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/features/tour-booking-enabled`);
        if (response.ok) {
          const data = await response.json();
          setTourBookingEnabled(data.enabled);
        }
      } catch (error) {
        console.error('Error fetching tour booking status:', error);
        // Default to enabled on error
        setTourBookingEnabled(true);
      }
    };

    fetchTourBookingStatus();
  }, []);

  const navigation = [
    { name: "About Opal", href: "/about-opal" },
    { name: "Coober Pedy", href: "/coober-pedy" },
    { name: "Free Museum", href: "/free-museum" },
    { name: "Underground Tours", href: "/underground-tours" },
    { name: "Schools / Groups", href: "/group-tours" },
    { name: "Opal Store", href: "/opal-store" },
    { name: "Webcams", href: "/webcam" },
    ...(tourBookingEnabled ? [{ name: "Tour Tickets", href: "/book-tour", isButton: true }] : []),
  ];

  return (
    <>
      {/* Top Info Bar */}
      <div className="bg-gray-50 border-b border-gray-200 text-gray-700 py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-orange-500" />
              <span className="font-medium">(08) 8672 5288</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              <OpeningHours compact={true} />
            </div>
          </div>
          <div className="hidden md:block">
            <span className="text-gray-600">📍 14 Hutchison St, Coober Pedy SA 5723</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="p-1 rounded-xl group-hover:shadow-xl transition-all duration-300">
                <img
                  src="/assets/umoonaico.png"
                  alt="Umoona Opal Mine Logo"
                  className="w-9 h-9"
                />
              </div>
              <div>
                <h1 className="text-base font-bold heritage-heading leading-tight">
                  Umoona Opal Mine
                </h1>
                <p className="text-xs text-gray-600 font-medium">& Museum</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-5">
              {navigation.map((item) => (
                item.isButton ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-[15px] font-semibold transition-all duration-300 transform hover:scale-105"
                  >
                    {item.name}
                  </Link>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`text-[15px] font-semibold transition-all duration-300 hover:text-orange-500 ${
                      location.pathname === item.href
                        ? "text-orange-500 border-b-2 border-orange-500 pb-1"
                        : "text-gray-700"
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              
              {/* Shopping Cart */}
              <CartButton />
              <CartSidebar />
            </nav>

            {/* Mobile Navigation */}
            <div className="lg:hidden flex items-center gap-2">
              <CartButton />
              <CartSidebar />
              
              {/* Mobile menu button */}
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-gray-700 hover:bg-gray-100">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 bg-white border-gray-200">
                  <div className="flex flex-col space-y-4 mt-8">
                    {navigation.map((item) => (
                      item.isButton ? (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setIsOpen(false)}
                          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-3 rounded-lg font-semibold transition-all duration-300 text-center"
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setIsOpen(false)}
                          className={`text-lg font-semibold transition-all duration-300 hover:text-orange-500 py-2 ${
                            location.pathname === item.href
                              ? "text-orange-500"
                              : "text-gray-700"
                          }`}
                        >
                          {item.name}
                        </Link>
                      )
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;